import React, { useMemo, useState } from "react"
import { Checkbox, Form, Input, InputNumber, Modal, message } from "antd"
import { organizationApi } from "../../Apis/organizationApi"
import {
  parseOrganizationApiError,
  unwrapOrganizationResult,
  buildPlanFeatureOptions,
  filterMemberFeaturesToPlan,
} from "../../utils/organizationApiHelpers"
import { translations } from "../../utils/Translation/staticTranslation"
import { getAppLanguage } from "../../utils/getAppLanguage"

const AddOrganizationMemberModal = ({
  open,
  onClose,
  onSuccess,
  allowedPlanFeatures = [],
  maxImages = 0,
  maxFurnitureFinder = 0,
  showFurnitureFinder = false,
}) => {
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)
  const lang = getAppLanguage()
  const t = translations[lang]?.organizationPage?.addMemberModal

  const featureOptions = useMemo(
    () => buildPlanFeatureOptions(allowedPlanFeatures, lang),
    [allowedPlanFeatures, lang],
  )

  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  const onFinish = async (values) => {
    setSubmitting(true)
    try {
      const payload = {
        email: String(values.email || "").trim().toLowerCase(),
        images: Number(values.images) || 0,
        allowed_features: filterMemberFeaturesToPlan(
          values.allowed_features || [],
          allowedPlanFeatures,
        ),
        language: lang === "ar" ? "AR" : "EN",
      }
      if (showFurnitureFinder) {
        payload.furniture_finder = Number(values.furniture_finder) || 0
      }
      const response = await organizationApi.addMember(payload)
      const result = unwrapOrganizationResult(response)
      message.success(result?.detail || t?.success)
      form.resetFields()
      onSuccess?.(result)
      onClose()
    } catch (error) {
      message.error(parseOrganizationApiError(error, lang))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      className="org-modal"
      title={t?.title || "Invite team member"}
      open={open}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
    >
      <p className="mb-20 text-14 text-8C8C80">{t?.subtitle}</p>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ images: 0, furniture_finder: 0, allowed_features: [] }}
      >
        <Form.Item
          name="email"
          label={t?.email}
          rules={[
            { required: true, message: t?.emailRequired },
            { type: "email", message: t?.emailInvalid },
          ]}
        >
          <Input placeholder={t?.emailPlaceholder} />
        </Form.Item>
        <Form.Item
          name="images"
          label={t?.images}
          extra={t?.imagesHint?.replace("{max}", String(maxImages))}
          rules={[
            {
              validator: (_, value) => {
                const n = Number(value) || 0
                if (n < 0) return Promise.reject(t?.imagesMin)
                if (maxImages >= 0 && n > maxImages) {
                  return Promise.reject(t?.imagesMax)
                }
                return Promise.resolve()
              },
            },
          ]}
        >
          <InputNumber min={0} max={maxImages} className="w-100" />
        </Form.Item>
        {showFurnitureFinder ? (
          <Form.Item
            name="furniture_finder"
            label={t?.furnitureFinder}
            extra={t?.furnitureFinderHint?.replace(
              "{max}",
              String(maxFurnitureFinder),
            )}
            rules={[
              {
                validator: (_, value) => {
                  const n = Number(value) || 0
                  if (n < 0) return Promise.reject(t?.furnitureFinderMin)
                  if (maxFurnitureFinder >= 0 && n > maxFurnitureFinder) {
                    return Promise.reject(t?.furnitureFinderMax)
                  }
                  return Promise.resolve()
                },
              },
            ]}
          >
            <InputNumber min={0} max={maxFurnitureFinder} className="w-100" />
          </Form.Item>
        ) : null}
        {featureOptions.length > 0 ? (
          <Form.Item
            name="allowed_features"
            label={t?.features}
            extra={t?.featuresFromPlan}
          >
            <Checkbox.Group
              className="org-feature-checkbox-group"
              options={featureOptions}
            />
          </Form.Item>
        ) : (
          <p className="text-14 text-A1A1A1 mb-16">{t?.noPlanFeatures}</p>
        )}
        <button
          type="submit"
          className="add-store org-modal-submit"
          disabled={submitting}
        >
          {submitting ? t?.sending : t?.submit}
        </button>
      </Form>
    </Modal>
  )
}

export default AddOrganizationMemberModal
